import { useCallback, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';

const APP_DOMAIN = import.meta.env.VITE_APP_DOMAIN || 'tracktoss.cc';

interface SharePlaylistProps {
  playlistId: string;
}

export default function SharePlaylist({ playlistId }: SharePlaylistProps) {
  const [showQrCode, setShowQrCode] = useState(false);
  const shareUrl = `https://${APP_DOMAIN}/playlist/${playlistId}`;

  const handleShare = useCallback(async () => {
    // Show the QR code first
    setShowQrCode(true);

    const shareMessage = `🎵 Join my playlist on TrackToss! Add your favorite songs and vote on what plays next. Let's create an amazing playlist together! 🎧\n\n${shareUrl}`;

    try {
      if (navigator.share) {
        console.log('Attempting navigator.share...');
        await navigator.share({
          title: 'TrackToss Playlist',
          text: shareMessage,
          url: shareUrl, // Add URL separately for better mobile support
        });
        console.log('navigator.share finished.');
      } else {
        // Fallback for browsers that don't support the Web Share API
        console.log('Web Share API not supported, falling back to clipboard.');
        await navigator.clipboard.writeText(shareMessage);
        alert('Playlist URL copied to clipboard!');
      }
    } catch (err) {
      console.error('Error during share or clipboard fallback:', err);
      // Keep the QR code visible even if sharing is cancelled/fails
    }
  }, [shareUrl]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleShare}
        className="flex cursor-pointer items-center gap-2 rounded bg-primary px-4 py-2 text-white transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50 active:bg-primary/90"
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
        </svg>
        Share Playlist
      </button>

      {showQrCode && (
        <div className="absolute right-0 top-full z-10 mt-2 rounded-lg border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-4 flex justify-center">
            <QRCodeSVG
              value={shareUrl}
              size={160}
              level="H"
              includeMargin
              className="rounded-lg bg-white p-2"
            />
          </div>
          <p className="mb-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Scan this code or use the shared link.
          </p>
          <button
            type="button"
            onClick={() => setShowQrCode(false)}
            className="w-full rounded bg-gray-200 px-3 py-1 text-sm text-gray-800 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
